import React, { useState, useEffect, useCallback } from 'react';
import { Users, TrendingUp, UserX, Clock, Network, CheckCircle, XCircle } from 'lucide-react';
import { dashboardAPI } from '../../services/api';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';

const COLORS = ['#16a34a', '#f59e0b', '#ef4444', '#6b7280'];

const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-100 dark:bg-gray-700 rounded-lg ${className}`} />
);

const EmptyState = ({ message }) => (
  <div className="text-center py-12 text-gray-400">
    <Network size={36} className="mx-auto mb-3 opacity-30" />
    <p className="text-sm">{message}</p>
  </div>
);

export default function ConnectionsAnalyticsPage({ timeRange = '30d' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dashboardAPI.getConnectionsAnalytics(timeRange);
      if (res.success) setData(res.data);
      else setError(res.error || 'Failed to load');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => { load(); }, [load]);

  const hasData = data && data.total > 0;

  const kpis = [
    {
      label: 'Total Connections',
      value: data?.total ?? 0,
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      sub: `${data?.totalInRange ?? 0} new this period`,
    },
    {
      label: 'Accepted',
      value: data?.accepted ?? 0,
      icon: CheckCircle,
      color: 'text-green-600',
      bg: 'bg-green-50',
      sub: `${data?.acceptanceRate ?? 0}% acceptance rate`,
    },
    {
      label: 'Pending',
      value: data?.pending ?? 0,
      icon: Clock,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      sub: 'Awaiting response',
    },
    {
      label: 'Blocked',
      value: data?.blocked ?? 0,
      icon: UserX,
      color: 'text-red-600',
      bg: 'bg-red-50',
      sub: `${data?.rejected ?? 0} rejected`,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <Network className="text-blue-600" size={24} />
            Network & Connections
          </h1>
          <p className="text-sm text-gray-500">
            Entrepreneur network growth and connection health
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-xl text-sm hover:border-blue-400 transition"
        >
          <TrendingUp size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl text-sm">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map(({ label, value, icon: Icon, color, bg, sub }) => (
          <div key={label} className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow">
            <div className={`w-8 h-8 ${bg} rounded-lg flex items-center justify-center mb-2`}>
              <Icon size={16} className={color} />
            </div>
            <p className="text-xs text-gray-500 mb-1">{label}</p>
            {loading ? (
              <Skeleton className="h-8 w-16 mb-1" />
            ) : (
              <p className={`text-2xl font-bold ${color}`}>
                {value.toLocaleString()}
              </p>
            )}
            <p className="text-xs text-gray-400 mt-1">{sub}</p>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Daily new connections trend */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
            New Connections Over Time
          </h3>
          <p className="text-xs text-gray-400 mb-4">
            Connection requests sent and accepted per period
          </p>
          {loading ? (
            <Skeleton className="h-48 w-full" />
          ) : !hasData ? (
            <EmptyState message="No connection data for this period." />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={data.perDay}>
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="newConnections"
                  stroke="#3b82f6"
                  fill="#bfdbfe"
                  name="New Requests"
                />
                <Area
                  type="monotone"
                  dataKey="accepted"
                  stroke="#16a34a"
                  fill="#bbf7d0"
                  name="Accepted"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Status breakdown pie */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
            Connection Status Breakdown
          </h3>
          <p className="text-xs text-gray-400 mb-4">
            Distribution of all connections by current status
          </p>
          {loading ? (
            <Skeleton className="h-48 w-full" />
          ) : !hasData ? (
            <EmptyState message="No connections recorded yet." />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={data.byStatus}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={({ label, percent }) =>
                    `${label} ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {(data.byStatus || []).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Network health bar */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
          Network Health Overview
        </h3>
        {loading ? (
          <Skeleton className="h-32 w-full" />
        ) : !hasData ? (
          <EmptyState message="Network health data will appear as users connect." />
        ) : (
          <div className="space-y-4">
            {[
              {
                label: 'Acceptance Rate',
                value: data.acceptanceRate,
                max: 100,
                color: 'bg-green-500',
                note: `${data.accepted} of ${data.total} connections accepted`,
              },
              {
                label: 'Block Rate',
                value: data.total > 0 ? Math.round((data.blocked / data.total) * 100) : 0,
                max: 100,
                color: 'bg-red-400',
                note: `${data.blocked} blocked connections`,
              },
              {
                label: 'Pending Rate',
                value: data.total > 0 ? Math.round((data.pending / data.total) * 100) : 0,
                max: 100,
                color: 'bg-amber-400',
                note: `${data.pending} awaiting response`,
              },
            ].map(({ label, value, color, note }) => (
              <div key={label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-600">{label}</span>
                  <span className="text-xs font-bold text-gray-800">{value}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full">
                  <div
                    className={`h-2 ${color} rounded-full transition-all duration-500`}
                    style={{ width: `${Math.min(value, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">{note}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Daily bar chart for detailed view */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Daily Connection Activity
        </h3>
        <p className="text-xs text-gray-400 mb-4">
          Granular view of requests and acceptances per day
        </p>
        {loading ? (
          <Skeleton className="h-48 w-full" />
        ) : !hasData ? (
          <EmptyState message="No activity data for this period." />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.perDay}>
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar
                dataKey="newConnections"
                fill="#3b82f6"
                radius={[3, 3, 0, 0]}
                name="New Requests"
              />
              <Bar
                dataKey="accepted"
                fill="#16a34a"
                radius={[3, 3, 0, 0]}
                name="Accepted"
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Info note if no data */}
      {!loading && !hasData && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
          <strong>Note:</strong> The connections table exists but has no records matching
          this time range. Data will populate as users connect on the mobile app.
          Try switching to <strong>All Time</strong> to see historical data.
        </div>
      )}
    </div>
  );
}